import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Save } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";

const money = (c) => (c == null ? "" : (c / 100).toFixed(2));

export default function AdminPricing() {
  const [labs, setLabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const navigate = useNavigate();

  const load = () => api.get("/admin/catalog").then(({ data }) => setLabs(data.labs)).catch((e) => toast.error(apiErr(e))).finally(() => setLoading(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const setField = (slug, field, value) => setLabs((ls) => ls.map((l) => l._id === slug ? { ...l, [field]: value } : l));

  const save = async (l) => {
    setSaving(l._id);
    try {
      await api.put(`/admin/catalog/${l._id}`, {
        monthly_price_cents: Math.round(Number(l.monthly_price_cents)),
        annual_price_cents: l.annual_price_cents === "" || l.annual_price_cents == null ? null : Math.round(Number(l.annual_price_cents)),
        monthly_enabled: !!l.monthly_enabled,
        annual_enabled: !!l.annual_enabled,
        active: !!l.active,
      });
      toast.success(`${l.name} pricing saved`);
      load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(null); }
  };

  if (loading) return <div className="h-screen grid place-items-center bg-background"><Loader2 className="h-7 w-7 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="admin-pricing-page">
      <div className="border-b border-border bg-card"><div className="max-w-5xl mx-auto px-6 py-4 flex items-center gap-3">
        <button onClick={() => navigate("/admin")} className="text-muted-foreground hover:text-foreground flex items-center gap-1.5 text-sm"><ArrowLeft className="h-4 w-4" /> Admin</button>
        <h1 className="text-lg font-bold ml-2">Lab Pricing</h1>
      </div></div>
      <div className="max-w-5xl mx-auto px-6 py-8 space-y-4">
        {labs.map((l) => (
          <div key={l._id} className="rounded-xl border border-border bg-card p-5" data-testid={`pricing-${l._id}`}>
            <div className="flex items-center justify-between mb-4">
              <div><div className="font-semibold">{l.name}</div><div className="text-xs text-muted-foreground">{l._id}</div></div>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!l.active} onChange={(e) => setField(l._id, "active", e.target.checked)} data-testid={`active-${l._id}`} /> Active</label>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="flex items-center gap-2 text-sm font-medium"><input type="checkbox" checked={!!l.monthly_enabled} onChange={(e) => setField(l._id, "monthly_enabled", e.target.checked)} data-testid={`monthly-en-${l._id}`} /> Monthly ($)</label>
                <input type="number" step="0.01" value={money(l.monthly_price_cents)} onChange={(e) => setField(l._id, "monthly_price_cents", Math.round(Number(e.target.value) * 100))} data-testid={`monthly-price-${l._id}`} className="w-full h-10 rounded-lg border border-border bg-background px-3 text-sm" />
              </div>
              <div className="space-y-1.5">
                <label className="flex items-center gap-2 text-sm font-medium"><input type="checkbox" checked={!!l.annual_enabled} onChange={(e) => setField(l._id, "annual_enabled", e.target.checked)} data-testid={`annual-en-${l._id}`} /> Annual ($)</label>
                <input type="number" step="0.01" placeholder="Not configured" value={money(l.annual_price_cents)} onChange={(e) => setField(l._id, "annual_price_cents", e.target.value === "" ? "" : Math.round(Number(e.target.value) * 100))} data-testid={`annual-price-${l._id}`} className="w-full h-10 rounded-lg border border-border bg-background px-3 text-sm" />
              </div>
            </div>
            <button onClick={() => save(l)} disabled={saving === l._id} data-testid={`save-${l._id}`} className="mt-4 h-9 px-4 rounded-lg bg-[#0078d4] hover:bg-[#106ebe] text-white text-sm font-medium flex items-center gap-1.5 disabled:opacity-60">
              {saving === l._id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
